import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URLS } from '../../config/api';
import { LeaderboardEntry, LeaderboardResponse, CrawlSummary } from '../../api/types';
import { SessionService } from '../../services/sessionService';
import { slugify } from '../../utils/urlUtils';
import { formatClock, formatShortDate, formatLongDate } from '../../utils/dateUtils';
import Markdown from '../../components/Markdown';
import StatTile from '../../components/ui/StatTile';
import './Leaderboard.css';

const INITIAL_VISIBLE = 10;
const SHOW_MORE_BATCH = 25;

const Leaderboard: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [crawl, setCrawl] = useState<CrawlSummary | null>(null);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [finishers, setFinishers] = useState(0);
  const [outOnCrawlNow, setOutOnCrawlNow] = useState(0);
  const [totalSessions, setTotalSessions] = useState(0);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const crawlsResponse = await fetch(API_URLS.CRAWLS());
        if (!crawlsResponse.ok) {
          throw new Error(`Failed to fetch crawls: ${crawlsResponse.status}`);
        }
        const crawlList: CrawlSummary[] = await crawlsResponse.json();
        const matched = crawlList.find(c => slugify(c.name) === slug);
        if (!matched) {
          setError('Crawl not found');
          return;
        }
        setCrawl(matched);

        const response = await fetch(API_URLS.LEADERBOARD(matched.id));
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: LeaderboardResponse = await response.json();
        setLeaderboardData(data.leaderboard);
        setFinishers(data.finishers);
        setOutOnCrawlNow(data.outOnCrawlNow);
        setTotalSessions(data.totalSessions);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        console.error('Error fetching crawl page data:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [slug]);

  const ownSessionId = SessionService.getSessionId();
  const visibleEntries = leaderboardData.slice(0, visibleCount);
  const ownIndex = ownSessionId
    ? leaderboardData.findIndex(entry => entry.sessionId === ownSessionId)
    : -1;
  const pinnedEntry = ownIndex >= visibleCount ? leaderboardData[ownIndex] : null;

  const renderRow = (entry: LeaderboardEntry, index: number) => (
    <tr key={entry.sessionId} className={index < 3 ? 'lb-podium' : ''}>
      <td className="lb-rank">{index + 1}</td>
      <td className="lb-name" data-testid="leaderboard-name">
        {entry.contenderName}
      </td>
      <td className="lb-pubs tabular">{entry.pubsCompleted}</td>
      <td className="lb-date">
        <span className="lb-date-short">
          {formatShortDate(entry.dateCompleted || entry.sessionStartTime)}
        </span>
        <span className="lb-date-long">
          {formatLongDate(entry.dateCompleted || entry.sessionStartTime)}
        </span>
      </td>
      <td className="lb-time tabular">{formatClock(entry.durationSeconds)}</td>
    </tr>
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h1 className="page-title" data-testid="page-title">
            Leaderboard
          </h1>
          <div className="lb-skeleton">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="lb-skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !crawl) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h1 className="page-title" data-testid="page-title">
            Leaderboard
          </h1>
          <div className="content-section">
            <p>This leaderboard couldn't be loaded. Check your signal, then try again.</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        <h1 className="page-title" data-testid="page-title">
          {crawl.name}
        </h1>
        {crawl.description && (
          <Markdown className="crawl-description" data-testid="crawl-description">
            {crawl.description}
          </Markdown>
        )}

        <div className="lb-section" data-testid="leaderboard">
          <h2 className="lb-eyebrow">Leaderboard</h2>
          {leaderboardData.length === 0 ? (
            <div className="lb-placeholder">
              <p>No entries on this leaderboard yet!</p>
            </div>
          ) : (
            <>
              <div className="lb-table-wrap">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th className="lb-rank">#</th>
                      <th className="lb-name">Name</th>
                      <th className="lb-pubs">Pubs</th>
                      <th className="lb-date">Date</th>
                      <th className="lb-time">Time</th>
                    </tr>
                  </thead>
                  <tbody>{visibleEntries.map((entry, index) => renderRow(entry, index))}</tbody>
                </table>
              </div>

              {visibleCount < leaderboardData.length && (
                <button
                  className="lb-show-more"
                  onClick={() => setVisibleCount(count => count + SHOW_MORE_BATCH)}
                >
                  Show {SHOW_MORE_BATCH} more
                </button>
              )}

              {pinnedEntry && (
                <div className="lb-pinned">
                  <div className="lb-pinned-label">Your best on this crawl</div>
                  <table className="lb-table lb-pinned-table">
                    <tbody>{renderRow(pinnedEntry, ownIndex)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="lb-section">
          <h2 className="lb-eyebrow">Crawl statistics</h2>
          <div className="lb-stats">
            <StatTile value={totalSessions} label="Started this crawl" />
            <StatTile value={finishers} label="Visited all pubs" />
            <StatTile value={outOnCrawlNow} label="Currently on this crawl" />
            <StatTile
              value={crawl.averageDurationHours > 0 ? `~${crawl.averageDurationHours}h` : '—'}
              label="Average finish time"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
